"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Mail, Phone, MapPin, FileText, Lock, Eye, Trash2, SlidersHorizontal, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const sections = [{
  id: "intro",
  title: "1. Introduction",
  icon: FileText,
  content: "I am Yassine Essaadi, trading as YE Consultancy, an independent freelance SEO consultant based in Swindon, UK. This privacy policy outlines how I handle personal information collected through this website (ye-consultancy.com) and through direct client communications. I respect your privacy and process personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018."
}, {
  id: "data-collected",
  title: "2. What personal data is collected",
  icon: Eye,
  content: "I only collect personal information that you choose to provide directly to me. This includes your name, email address, telephone number, website URL, and project notes submitted via the consultation contact form or sent directly via email or telephone calls. When you browse the website, standard technical connection data such as basic device and browser identifiers may be logged automatically by the web server for security and operational monitoring."
}, {
  id: "data-usage",
  title: "3. How the data is used",
  icon: SlidersHorizontal,
  content: "I use your personal details exclusively to respond to your initial enquiries, prepare SEO proposals, deliver consultancy and audit services, manage invoicing, and communicate project updates. If you explicitly consent to receive SEO industry updates or insights, I may contact you with relevant educational content. I never sell, rent, or trade your personal data with any third party for marketing or advertising purposes."
}, {
  id: "cookies",
  title: "4. Cookies and analytics",
  icon: Lock,
  content: "This website uses standard first-party and performance cookies to understand how visitors interact with site pages, identify navigation issues, and ensure fast page loading times. These analytics scripts collect aggregated, anonymised statistics such as page visits and time on page, without creating persistent individual user profiles. You can adjust your browser settings at any time to decline or remove non-essential cookies."
}, {
  id: "processors",
  title: "5. Third-party tools and processors",
  icon: ShieldCheck,
  content: "To operate this website and deliver professional client deliverables, I rely on trusted service providers including secure cloud website hosting, encrypted email infrastructure, and professional SEO analytics platforms. All third-party providers are vetted to ensure they adhere to stringent data protection standards and UK GDPR compliance requirements."
}, {
  id: "retention",
  title: "6. Data retention",
  icon: FileText,
  content: "I retain personal information only for as long as necessary to fulfill the purposes for which it was collected. Prospective client enquiries that do not result in an engagement are deleted after twelve months. Records relating to contracted client work, audit deliverables, and invoicing are securely retained for up to six years to comply with statutory UK accounting and legal obligations."
}, {
  id: "rights",
  title: "7. Your data subject rights",
  icon: Trash2,
  content: "Under UK GDPR, you have statutory rights concerning your personal data. These include the right to request access to the personal data I hold about you, request the correction of inaccurate details, request the erasure of your data where no overriding legal obligation exists, request restriction of processing, and object to processing. To exercise any of these rights, contact me directly using the details provided below."
}];
const rightsList = ["Right of access to your personal information", "Right to rectification of inaccurate or incomplete records", "Right to erasure ('right to be forgotten')", "Right to restrict or object to processing", "Right to lodge a complaint with the UK Information Commissioner's Office (ICO)"];
export default function Privacy() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="privacypolicy" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Navigation Breadcrumb / Back button */}
        <div className="mb-8 flex items-center justify-between">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/HomePage" className="text-muted-foreground hover:text-foreground">
              <span className="flex items-center gap-1.5">
                <ChevronRight className="size-4 rotate-180" />
                Back to Home
              </span>
            </Link>
          </Button>

          <Badge variant="secondary" className="font-normal text-xs">
            UK GDPR Compliant
          </Badge>
        </div>

        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="border-b border-border pb-8 mb-12">
          <div className="flex items-center gap-2 text-primary font-medium text-sm mb-3">
            <ShieldCheck className="size-4" />
            <span>YE Consultancy Legal Notice</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Privacy policy
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mb-4 leading-relaxed">
            How I collect, use, and protect your personal data when you visit this website, submit an enquiry, or work directly with me on SEO consultancy projects.
          </p>
          <p className="text-xs text-muted-foreground font-mono">
            Last updated: October 24, 2024
          </p>
        </motion.div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main Legal Clauses */}
          <div className="lg:col-span-8 space-y-10">
            {sections.map((sec, i) => {
            const IconComponent = sec.icon;
            return <article key={sec.id} data-index={i} id={sec.id} className="space-y-3 scroll-mt-24">
                  <div className="flex items-center gap-2.5">
                    <div className="flex size-7 items-center justify-center rounded-md bg-secondary text-primary">
                      <IconComponent className="size-4" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                      {sec.title}
                    </h2>
                  </div>
                  <p className="text-muted-foreground leading-relaxed text-base">
                    {sec.content}
                  </p>

                  {sec.id === "rights" && <div className="mt-4 rounded-lg bg-card border border-border p-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Summary of your statutory rights:
                      </p>
                      <ul className="space-y-2 text-sm text-foreground">
                        {rightsList.map((right, index) => <li key={right} data-index={index} className="flex items-start gap-2">
                            <span className="text-primary font-bold">•</span>
                            <span>{right}</span>
                          </li>)}
                      </ul>
                    </div>}

                  <Separator className="pt-2" />
                </article>;
          })}

            {/* Section 8: Contact details */}
            <article id="contact" className="space-y-4 pt-2">
              <div className="flex items-center gap-2.5">
                <div className="flex size-7 items-center justify-center rounded-md bg-secondary text-primary">
                  <Mail className="size-4" />
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                  8. Contact details for privacy queries
                </h2>
              </div>
              <p className="text-muted-foreground leading-relaxed text-base">
                If you have questions about this policy, need to update your contact preferences, or wish to exercise your data subject rights, please reach out directly to me:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center gap-2 text-primary text-xs font-semibold mb-1">
                    <Mail className="size-3.5" />
                    Email
                  </div>
                  <a href="mailto:contact@ye-consultancy.com" className="text-sm font-medium text-foreground hover:text-primary transition-colors break-all">
                    contact@ye-consultancy.com
                  </a>
                </div>

                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center gap-2 text-primary text-xs font-semibold mb-1">
                    <Phone className="size-3.5" />
                    Direct phone
                  </div>
                  <a href="tel:+447490984430" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                    +44 7490984430
                  </a>
                </div>

                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center gap-2 text-primary text-xs font-semibold mb-1">
                    <MapPin className="size-3.5" />
                    Location
                  </div>
                  <address className="not-italic text-sm text-muted-foreground">
                    Windmill Hill Business Park, Whitehill Way, Swindon SN5 6QR, UK
                  </address>
                </div>
              </div>
            </article>
          </div>

          {/* Sidebar / Quick Navigation Card */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <Card className="border-border shadow-xs">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">
                    Policy overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      Data Controller
                    </p>
                    <p className="font-medium text-foreground">
                      Yassine Essaadi
                    </p>
                    <p className="text-xs text-muted-foreground">
                      YE Consultancy (Independent Freelance Consultant)
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      Supervisory Authority
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Information Commissioner's Office (ICO), United Kingdom.
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      Direct consultation
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Need an SEO audit or strategy discussion? Enquiries are confidential.
                    </p>
                    <Button variant="default" size="sm" className="w-full mt-2" asChild>
                      <Link to="/ContactPage">
                        Get in touch
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Jump Links */}
              <div className="rounded-xl border border-border bg-muted/40 p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Document sections
                </h3>
                <nav className="space-y-1.5 text-xs">
                  {sections.map((sec, idx) => <Link key={sec.id} data-index={idx} to={`#${sec.id}`} className="block text-muted-foreground hover:text-primary transition-colors py-1">
                      {sec.title}
                    </Link>)}
                  <Link to="#contact" className="block text-muted-foreground hover:text-primary transition-colors py-1">
                    8. Contact details
                  </Link>
                </nav>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}